import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useBooking } from "@/context/BookingContext";

const links = [
  { id: "manifesto", label: "Ethos" },
  { id: "services", label: "Services" },
  { id: "hair-showcase", label: "Hair" },
  { id: "gallery", label: "Gallery" },
  { id: "branches", label: "Visit" },
];

export const Navbar = () => {
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 360);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || menuOpen ? "bg-cream/90 backdrop-blur-xl border-b border-pink/20 py-4" : "py-6"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        {/* Left: desktop links + mobile hamburger */}
        <div className="justify-self-start flex items-center min-w-0">
          <div className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}-link`}
                onClick={() => go(l.id)}
                className="group relative font-mono text-xs uppercase tracking-[0.2em] text-pink/80 hover:text-pink transition-colors whitespace-nowrap"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-pink transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>
          <button
            className="lg:hidden text-pink"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Center: logo */}
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="justify-self-center px-2 font-serif text-2xl sm:text-4xl md:text-5xl tracking-[0.16em] text-pink font-semibold whitespace-nowrap"
        >
          FABROWZ
        </button>

        {/* Right: book */}
        <div className="justify-self-end min-w-0">
          <button
            data-testid="nav-book-button"
            onClick={() => openBooking()}
            className="group relative overflow-hidden rounded-full border border-ink bg-ink px-4 sm:px-6 py-2.5 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-cream transition-colors"
          >
            <span className="relative z-10 group-hover:text-ink transition-colors duration-300">Book</span>
            <span className="hidden sm:inline relative z-10 group-hover:text-ink transition-colors duration-300">&nbsp;Treatment</span>
            <span className="absolute inset-0 z-0 origin-left scale-x-0 bg-pink transition-transform duration-300 group-hover:scale-x-100" />
          </button>
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-5 pt-4 pb-2 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}-link`}
                  onClick={() => go(l.id)}
                  className="text-left py-3 font-mono text-sm uppercase tracking-[0.2em] text-pink border-b border-pink/10"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
